import { isPlatformStaffRole } from '@/utils/teamRoles';

/** Plan-backed source limits (mirrors backend `src/services/plans.py`). */
export type SourcePlanLimits = {
  maxAccessibleSources: number;
  maxSourcesPerSearch: number;
};

const PLAN_SOURCE_LIMITS: Record<string, SourcePlanLimits> = {
  free: { maxAccessibleSources: 2, maxSourcesPerSearch: 2 },
  starter: { maxAccessibleSources: 5, maxSourcesPerSearch: 5 },
  pro: { maxAccessibleSources: 20, maxSourcesPerSearch: 20 },
  business: { maxAccessibleSources: 100, maxSourcesPerSearch: 100 },
};

/** Matches backend `UNLIMITED_INT` sentinel. */
export const UNLIMITED_SOURCE_LIMIT = 999_999;

export function sourceLimitsForPlan(plan?: string | null): SourcePlanLimits {
  const key = (plan ?? 'free').trim().toLowerCase();
  return PLAN_SOURCE_LIMITS[key] ?? PLAN_SOURCE_LIMITS.free;
}

export function isUnlimitedSourceLimit(value?: number | null): boolean {
  return value != null && value >= UNLIMITED_SOURCE_LIMIT;
}

export function formatAccessibleSourceAccess(user: {
  role?: string;
  plan?: string;
  assigned_source_count?: number;
  implicit_all_sources?: boolean;
  max_sources?: number;
  max_sources_per_search?: number;
}): string {
  if (user.role && isPlatformStaffRole(user.role)) {
    return 'All sources via role';
  }
  const count = user.assigned_source_count ?? 0;
  const limits = sourceLimitsForPlan(user.plan);
  const accessibleLimit = user.max_sources ?? limits.maxAccessibleSources;
  const perSearch = user.max_sources_per_search ?? limits.maxSourcesPerSearch;
  const accessibleLabel = count === 1 ? '1 accessible' : `${count} accessible`;
  const over = count > accessibleLimit;
  if (over) {
    return `${accessibleLabel} (limit ${accessibleLimit}) · ${perSearch}/search`;
  }
  return `${accessibleLabel} · ${perSearch}/search limit`;
}

/** Display source access using effective workspace plan when available. */
export function formatWorkspaceSourceAccess(user: {
  role?: string;
  plan?: string;
  assigned_source_count?: number;
  implicit_all_sources?: boolean;
  max_sources?: number;
  max_sources_per_search?: number;
  workspace_unlimited?: boolean;
  organizationNames?: string[];
}): string {
  if (
    user.workspace_unlimited ||
    isUnlimitedSourceLimit(user.max_sources) ||
    (user.implicit_all_sources && (user.organizationNames?.length ?? 0) > 0)
  ) {
    return 'Unlimited via workspace plan';
  }
  if (user.implicit_all_sources || (user.role && isPlatformStaffRole(user.role))) {
    return 'All sources via role';
  }
  return formatAccessibleSourceAccess(user);
}

export function searchSourceLimitMessage(
  accessibleCount: number,
  maxPerSearch: number,
): string {
  if (accessibleCount > maxPerSearch) {
    return `You can access ${accessibleCount} source${accessibleCount === 1 ? '' : 's'}. Your plan allows selecting up to ${maxPerSearch} sources per search.`;
  }
  return `Your plan allows up to ${maxPerSearch} sources per search.`;
}

export function accessibleSourceLimitMessage(plan: string, limit: number, selected: number): string {
  const label = plan.trim().toLowerCase() === 'free' ? 'Free' : plan;
  return `${label} plan allows only ${limit} accessible source${limit === 1 ? '' : 's'}. Upgrade the user or remove an existing source (${selected} selected).`;
}
